import { InertiaLinkProps, Link as InertiaLink } from '@inertiajs/react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

import { buttonVariants } from './buttonVariants';
import { Spinner } from './Spinner';
import { Tooltip, TooltipTrigger } from './Tooltip';
import { TooltipContent } from './TooltipContent';
import { TooltipProvider } from './TooltipProvider';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  tooltip?: string;
}

const Button = ({
  className,
  variant,
  size,
  disabled = false,
  asChild = false,
  loading = false,
  tooltip,
  ref,
  ...props
}: ButtonProps & { ref?: React.Ref<HTMLButtonElement> }) => {
  const Comp = asChild ? Slot : 'button';
  const buttonElement = (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
      disabled={disabled || loading}
    >
      {loading ? <Spinner size="md" inline /> : null}
      <Slottable>{size === 'icon' && loading ? null : props.children}</Slottable>
    </Comp>
  );

  if (tooltip) {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>{buttonElement}</TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return buttonElement;
};
Button.displayName = 'Button';

export type LinkProps = VariantProps<typeof buttonVariants> &
  Omit<InertiaLinkProps, 'size'> & {
    loading?: boolean;
    tooltip?: string;
  };

const ButtonLink = ({
  className,
  loading,
  tooltip,
  variant = 'default',
  size = 'default',
  ref,
  ...props
}: LinkProps & { ref?: React.Ref<HTMLAnchorElement> }) => {
  const linkElement = (
    <InertiaLink
      role="link"
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    >
      {loading ? <Spinner size="md" inline /> : null}
      {props.children}
    </InertiaLink>
  );

  if (tooltip) {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>{linkElement}</TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return linkElement;
};
ButtonLink.displayName = 'ButtonLink';

export { Button, ButtonLink };
